import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {alpha, Grid, ThemeProvider} from "@mui/material";

import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import {Wheel} from "react-custom-roulette";
import UserService from "../../../api/userService";
import {toastError} from "../../shared/toast/Toast";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import {getRandomNumber} from "../../../util/utils";
import genie_logo_short from "../../../assets/images/logo-short.svg";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import {spinTheWheel} from "../../../store/auth/Slice";
import ReactGA from "react-ga4";
import {useLocation} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import {toggleSpinTheWheel} from "../../../store/app/Slice";
import {useStore} from "react-redux";

const fontWeight = 'bold';
const fontSize = 35;
const textDistance = 80;
const spinDuration = 0.5;

interface IPrize {
    points_award: number
    points_index: number
}

const SpinTheWheelDialog = () => {
    const open = useAppSelector((state: RootState) => state.appReducer.spinTheWheelOpen);
    const store = useStore<RootState>();
    const theme = useTheme()
    const purpleBackground = {backgroundColor: theme.palette.quaternary.main, textColor: theme.palette.primary.main}
    const whiteBackground = {backgroundColor: theme.palette.primary.main, textColor: theme.palette.quaternary.main}
    const goldBackground = {backgroundColor: theme.palette.tertiary.main, textColor: theme.palette.primary.main}
    const data = [
        {option: '2', style: whiteBackground},
        {option: '1', style: purpleBackground},
        {option: '3', style: whiteBackground},
        {option: '1', style: purpleBackground},
        {option: '2', style: whiteBackground},
        {option: '1', style: purpleBackground},
        {option: '5', style: whiteBackground},
        {option: '3', style: purpleBackground},
        {option: '1', style: whiteBackground},
        {option: '2', style: purpleBackground},
        {option: '1', style: whiteBackground},
        {option: '5', style: purpleBackground},
        {option: '2', style: whiteBackground},
        {option: '3', style: purpleBackground},
        {option: '1', style: whiteBackground},
        {option: '10', style: goldBackground},
        {option: '1', style: whiteBackground},
        {option: '3', style: purpleBackground},
        {option: '2', style: whiteBackground},
        {option: '1', style: purpleBackground},
    ];
    const {t} = useTranslation()
    const [mustSpin, setMustSpin] = useState(false);
    const [showText, setShowText] = useState(false)
    const [prize, setPrize] = useState<IPrize>({points_award: 1, points_index: 0});
    const dispatch = useAppDispatch()

    const {pathname} = useLocation()

    const shouldOpenSpinTheWheel = useMemo(() => pathname === '/' || pathname.includes('/profile'), [pathname])

    const getRandomWinnerFromIndex = (prizeIndex: 0 | 1 | 2 | 3 | 4) => {
        const arrayFor1 = [1, 3, 5, 8, 10, 14, 16, 19]
        const arrayFor2 = [0, 4, 9, 12, 18]
        const arrayFor3 = [2, 7, 13, 17]
        const arrayFor5 = [6, 11]

        switch (prizeIndex) {
            case 0:
                return arrayFor1[getRandomNumber(0, arrayFor1.length - 1)]
            case 1:
                return arrayFor2[getRandomNumber(0, arrayFor2.length - 1)]
            case 2:
                return arrayFor3[getRandomNumber(0, arrayFor3.length - 1)]
            case 3:
                return arrayFor5[getRandomNumber(0, arrayFor5.length - 1)]
            default:
                return 15
        }
    }

    const handleSpinClick = () => {
        if (!mustSpin) {
            UserService.spinTheWheel().then((res) => {
                setPrize({
                    ...res.data,
                    points_index: getRandomWinnerFromIndex(res.data.points_index)
                })
                setMustSpin(true)
                const serverTime = store.getState().appReducer.serverTime ?? new Date().toISOString()
                ReactGA.event("earn_virtual_currency", {virtual_currency_name: 'Points', value: res.data.points_award})
                dispatch(spinTheWheel({points_award: res.data.points_award, server_time: serverTime}))
            }).catch((e) => {
                toastError(e.response?.data?.detail)
            })
        }
    };

    const handleDialogClose = () => {
        dispatch(toggleSpinTheWheel(false))
    }

    const dialogContent = (
        <>
            <Typography variant="body1" color="primary" mb={3}>{t('spin_the_wheel_explanation')}</Typography>
            <Grid container justifyContent="center" mb="2em" position="relative" sx={{
                "& > :first-child": {
                    width: '58vw',
                    height: '58vw',
                }
            }}>
                <Wheel
                    mustStartSpinning={mustSpin}
                    prizeNumber={prize.points_index}
                    data={data}
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    outerBorderWidth={0}
                    innerRadius={20}
                    innerBorderWidth={0}
                    radiusLineColor={theme.palette.secondary.main}
                    radiusLineWidth={1}
                    spinDuration={spinDuration}
                    startingOptionIndex={2}
                    // perpendicularText
                    textDistance={textDistance}
                    onStopSpinning={() => {
                        setMustSpin(false);
                        setShowText(true)
                    }}
                />
                <img src={genie_logo_short} width={28} alt="genie-logo"
                     style={{position: 'absolute', maxWidth: '4%', zIndex: '5', top: '44%', minWidth: '16px'}}/>
            </Grid>
            {!showText ? <Grid container justifyContent="center">
                    <LoadingButton loading={mustSpin} variant="contained" size="large" onClick={handleSpinClick}
                                   sx={{
                                       minWidth: 200,
                                       textTransform: "uppercase"
                                   }}>{t('spin_the_wheel')}</LoadingButton>
                </Grid> :
                <>
                    <Typography color="primary" variant="h5" fontWeight={600}
                                textAlign="center">{t('points_congratulations', {count: prize.points_award})}</Typography>
                    <Typography sx={{color: alpha(theme.palette.primary.main, .8)}} variant="subtitle1"
                                textAlign="center">{t('continue_browsing')}</Typography>
                </>
            }

        </>
    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={open && shouldOpenSpinTheWheel}
                          title={t('wheel_spins_dialog_title')}
                          maxWidth='md'
                          handleDialogClose={handleDialogClose}
                          content={dialogContent}
                          contentSx={{overflowX: 'hidden'}}/>
        </ThemeProvider>
    )
}

export default SpinTheWheelDialog
